/** @jsx jsx */
import React, {
	useCallback,
	useEffect,
	useState,
	useRef,
	type MouseEvent,
	useMemo,
	type ReactNode,
} from 'react';
import { styled, jsx } from '@compiled/react';
import { isMacOs } from '@jira/platform__os-detection/src/isMacOs.tsx';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import { getDocument } from '@atlaskit/browser-apis';
import { cssMap } from '@atlaskit/css';
import { fg } from '@atlassian/jira-feature-gating';
import { useCommandPalette } from '../../../../controllers/command-palette/index.tsx';
import type { ListedCommand, CommandPaletteBodyProps } from '../../../types/commands/index.tsx';
import {
	getAnalyticsAttributesFromResult,
	getRegistryData,
	isSecondaryTrigger,
} from '../../../utils.tsx';
import { CommandPaletteSearchResultGroupItem } from '../../command-palette-search-dropdown/command-palette-search-result-group-item/index.tsx';
import { CommandPaletteSearchResultListItem } from '../../command-palette-search-dropdown/command-palette-search-result-list-item/index.tsx';
import { CommandPaletteSearchResultListWrapper } from '../../command-palette-search-dropdown/command-palette-search-result-list-wrapper/index.tsx';
import { COMPAL_MENU_ITEM_ID } from '../../command-palette-search-dropdown/constants.tsx';
import { CommandRowErrorBoundary } from './command-row-error-boundary/index.tsx';
import { CommandRow } from './command-row/index.tsx';
import {
	COMMAND_CONTAINER_ROW_TEST_ID,
	GROUP_TEST_ID,
	NO_RESULTS_TEST_ID,
	SCROLL_DIRECTION,
} from './constants.tsx';
import { getScrollToIndex, isElementVisibleInContainer, scrollResultIntoView } from './utils.tsx';

const styles = cssMap({
	group: {
		display: 'flex',
		gap: token('space.050'),
		flexDirection: 'column',
	},
});

type CommandPaletteChildrenProps = {
	results: CommandPaletteBodyProps['results'];
	noResultsFallback?: string | JSX.Element | null | undefined;
	renderAfterCommands?: CommandPaletteBodyProps['renderAfterCommands'];
	resultsRefs: React.MutableRefObject<HTMLDivElement[]>;
};

const CommandPaletteChildren = ({
	results,
	noResultsFallback,
	renderAfterCommands,
	resultsRefs,
}: CommandPaletteChildrenProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [
		{ isSearchFieldFocused, loadingList, registry, activeRegistry },
		{ setActiveIndex, removeCommandsById, executeCommand },
	] = useCommandPalette();

	const onHoverCommand = useCallback(
		(index?: number) => {
			if (index === undefined) return;
			setActiveIndex(index);
		},
		[setActiveIndex],
	);

	const onMouseDownCommand = useCallback((event: MouseEvent) => {
		event.preventDefault();
	}, []);

	const onClickCommand = useCallback(
		(event: MouseEvent | React.KeyboardEvent, result: ListedCommand) => {
			executeCommand(
				result.command,
				{
					method: 'mouse',
					createAnalyticsEvent,
					attributes: {
						...getAnalyticsAttributesFromResult(result),
						...getRegistryData(registry, activeRegistry, result?.command.id),
					},
				},
				isSecondaryTrigger(event),
			);
		},
		[activeRegistry, createAnalyticsEvent, executeCommand, registry],
	);

	const onClickKeyboardCommand = useCallback(
		(event: KeyboardEvent, result: ListedCommand) => {
			executeCommand(
				result.command,
				{
					method: 'keyboard',
					createAnalyticsEvent,
					attributes: {
						...getAnalyticsAttributesFromResult(result),
						...getRegistryData(registry, activeRegistry, result.command.id),
					},
				},
				isMacOs() ? event.metaKey : event.ctrlKey,
			);
		},
		[activeRegistry, createAnalyticsEvent, executeCommand, registry],
	);

	if (fg('dee-8683-compal-a11y-button-keyboard-accessibility')) {
		// eslint-disable-next-line  react-hooks/rules-of-hooks
		useEffect(() => {
			const handleEnterKey = (event: KeyboardEvent) => {
				if (event.key === 'Enter') {
					const focusedElement = typeof document !== 'undefined' ? document.activeElement : null;
					let targetElement = focusedElement;
					while (targetElement && targetElement !== getDocument()?.body) {
						if (targetElement.getAttribute('data-testid') === COMMAND_CONTAINER_ROW_TEST_ID) {
							event.preventDefault();
							const focusedId = targetElement.id?.replace(`${COMPAL_MENU_ITEM_ID}-`, '');
							if (focusedId) {
								const selectedCommand = results.find(
									(result): result is ListedCommand =>
										!result.isGroup && 'command' in result && result.command.id === focusedId,
								);
								if (selectedCommand) {
									onClickKeyboardCommand(event, selectedCommand);
								}
							}
							break;
						}
						targetElement = targetElement.parentElement;
					}
				}
			};

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.addEventListener('keydown', handleEnterKey);

			return () => {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.removeEventListener('keydown', handleEnterKey);
			};
		}, [onClickKeyboardCommand, results]);
	}

	const localResultRefs = resultsRefs.current;

	const commandItem = useCallback(
		(result: ListedCommand, index: number) => (
			<CommandRowErrorBoundary
				key={index}
				teamName={typeof registry?.teamName === 'string' ? registry.teamName : 'deliveroo'}
				onError={() => removeCommandsById([result.command.id])}
				attributes={getRegistryData(registry, activeRegistry, result?.command.id)}
			>
				<CommandPaletteSearchResultListItem
					ref={(commandRef) => {
						if (commandRef) {
							localResultRefs[index] = commandRef;
						}
					}}
					id={result.command.id}
					active={result.active}
					key={index}
				>
					<CommandRow
						index={index}
						isSearchFieldFocused={isSearchFieldFocused}
						result={result}
						onHoverCommand={(ev) => {
							// Safari is weird and likes to fire mouseover events when the mouse is not moving
							if (ev.movementX === 0 && ev.movementY === 0) return;
							onHoverCommand(result.index);
						}}
						onClickCommand={(event) => onClickCommand(event, result)}
						onMouseDownCommand={onMouseDownCommand}
					/>
				</CommandPaletteSearchResultListItem>
			</CommandRowErrorBoundary>
		),
		[
			activeRegistry,
			isSearchFieldFocused,
			localResultRefs,
			onClickCommand,
			onHoverCommand,
			onMouseDownCommand,
			registry,
			removeCommandsById,
		],
	);

	const renderGroupedResults = useCallback(() => {
		const resultList = [];
		let groupList: ReactNode[] = [];

		const wrappedGroup = (group: ReactNode[], groupIndex: number) => (
			<div
				role="group"
				aria-labelledby={`group-heading-${groupIndex}`}
				key={`group-${groupIndex}`}
				css={styles.group}
			>
				{group}
			</div>
		);

		results.forEach((result, i) => {
			if (result.isGroup) {
				if (groupList.length !== 0) {
					resultList.push(wrappedGroup(groupList, resultList.length));
					groupList = [];
				}
				groupList.push(
					<CommandPaletteSearchResultGroupItem
						ref={(commandRef) => {
							if (commandRef) {
								localResultRefs[i] = commandRef;
							}
						}}
						group={result.group}
						key={i}
					>
						<GroupContainerComponent
							as="h2"
							role="presentation"
							data-testid={GROUP_TEST_ID}
							isFirstResult={i === 0}
							id={`group-heading-${resultList.length}`}
						>
							{result.renderGroup ? result.renderGroup() : result.group}
						</GroupContainerComponent>
					</CommandPaletteSearchResultGroupItem>,
				);
			} else {
				groupList.push(commandItem(result, i));
			}
		});

		if (groupList.length !== 0) {
			resultList.push(wrappedGroup(groupList, resultList.length));
		}
		return resultList;
	}, [commandItem, localResultRefs, results]);

	return (
		<>
			{!loadingList && noResultsFallback && (
				<NoResultsContainer data-testid={NO_RESULTS_TEST_ID}>
					{noResultsFallback}
				</NoResultsContainer>
			)}
			{renderGroupedResults()}
			{renderAfterCommands?.({})}
		</>
	);
};

export const CommandPaletteBody = ({
	results,
	total,
	fallback,
	renderAfterCommands,
}: CommandPaletteBodyProps): React.JSX.Element => {
	const [{ activeIndex, isSearchFieldFocused, expandedCommand, search }, { setActiveIndex }] =
		useCommandPalette();

	const [prevActiveIndex, setPrevActiveIndex] = useState(activeIndex);

	const resultsRefs = useRef<HTMLDivElement[]>([]);
	const modalBodyRef = useRef<HTMLDivElement>(null);

	const onArrowScroll = useCallback(
		(newActiveIndex: number) => {
			setActiveIndex(newActiveIndex);
		},
		[setActiveIndex],
	);

	// eslint-disable-next-line react-you-might-not-need-an-effect/no-reset-all-state-on-prop-change
	useEffect(() => {
		if (!modalBodyRef.current || prevActiveIndex === activeIndex) return;
		// eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
		setPrevActiveIndex(activeIndex);

		const scrollTo: SCROLL_DIRECTION =
			activeIndex > prevActiveIndex ? SCROLL_DIRECTION.END : SCROLL_DIRECTION.START;

		const scrollToIndex = getScrollToIndex(results, activeIndex, scrollTo);
		const scrolledToResult = resultsRefs.current[scrollToIndex];
		if (scrolledToResult && !isElementVisibleInContainer(scrolledToResult, modalBodyRef.current)) {
			const lastIndex = results.length - 1;
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
			const firstIndex = results.findIndex((result) => !result.isGroup);

			if (lastIndex === scrollToIndex) {
				modalBodyRef.current.scrollTo(0, modalBodyRef.current.scrollHeight);
			} else if (firstIndex === scrollToIndex) {
				modalBodyRef.current.scrollTo(0, 0);
			} else {
				scrollResultIntoView({
					target: scrolledToResult,
					direction: scrollTo,
					scrollContainer: modalBodyRef.current,
				});
			}
		}
	}, [activeIndex, prevActiveIndex, results]);

	useEffect(() => {
		if (!modalBodyRef.current?.scrollTo) return;
		modalBodyRef.current.scrollTo(0, 0);
	}, [expandedCommand, search]);

	useEffect(() => {
		// Replace with lodash/noop
		// eslint-disable-next-line @typescript-eslint/no-empty-function
		if (!isSearchFieldFocused) return () => {};

		const handleArrowScroll = (ev: KeyboardEvent) => {
			if (ev.key === 'ArrowUp') {
				ev.preventDefault();
				activeIndex > 0 && onArrowScroll(activeIndex - 1);
			}
			if (ev.key === 'ArrowDown') {
				ev.preventDefault();
				activeIndex < total - 1 && onArrowScroll(activeIndex + 1);
			}
		};

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.addEventListener('keydown', handleArrowScroll);

		return () => {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.removeEventListener('keydown', handleArrowScroll);
		};
	}, [isSearchFieldFocused, total, activeIndex, setActiveIndex, onArrowScroll]);

	const noResultsFallback = useMemo(
		() => fallback?.onNoResults?.renderLabel?.() || fallback?.onNoResults?.label,
		[fallback],
	);

	return (
		<CommandPaletteSearchResultListWrapper ref={modalBodyRef} noResultsFallback={noResultsFallback}>
			<CommandPaletteChildren
				results={results}
				noResultsFallback={noResultsFallback}
				renderAfterCommands={renderAfterCommands}
				resultsRefs={resultsRefs}
			/>
		</CommandPaletteSearchResultListWrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GroupContainerComponent = styled.div<{ isFirstResult?: boolean }>({
	font: token('font.body'),
	color: token('color.text.subtlest'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	padding: ({ isFirstResult }) =>
		`${isFirstResult ? token('space.100') : token('space.150')} ${token('space.300')} 0 ${token(
			'space.300',
		)}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const NoResultsContainer = styled.div({
	textAlign: 'center',
	color: token('color.text.subtlest'),
	paddingTop: token('space.400'),
	paddingRight: token('space.200'),
	paddingBottom: token('space.400'),
	paddingLeft: token('space.200'),
});
